import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getInputParametersBySubInterface } from 'data/protocolInputParameters';
import { DeviceConfig } from 'shared/models';
import { CheckField, LegacySelectField, TextField } from 'ui/shared/controls';

require('./InputParametersField.css');

export type Props = {
    config: DeviceConfig;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
    subinterface: string | null;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
};

const nullValue = 'none';

export const InputParametersField = ({ config, className, disabled = false, readOnly = false, subinterface, onChange }: Props) => {
    const { t } = useTranslation();
    const parameters = getInputParametersBySubInterface(subinterface, config.properties.params);
    
    const handleChangeSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        onChange({ currentTarget: {
            value: e.currentTarget.value,
            name: e.currentTarget.name,
            type: 'number',
        } } as ChangeEvent<HTMLInputElement>);
    };

    return (
        <div
            id="input-parameters-field"
            className={ `field ${className || ''} ${disabled ? 'disabled' : ''}` }
        >
            <label>
                { t('DEVICE_CONFIGS.DIALOG.FIELD.PARAMETERS') }
            </label>
            <div className="_parameter-container">
                { typeof subinterface === 'string' && typeof typeof parameters === 'undefined' &&
                    <div className="_empty">
                        { t('LABEL.UNKNOWN_SUBVAR', [ subinterface ]) }
                    </div>
                }
                { (!subinterface || !parameters?.length) &&
                    <div className="_empty">
                        { t('NONE') }
                    </div>
                }
                { parameters && parameters.map(inputParameter => {
                    if (inputParameter.dataType === 'number') {
                        return (
                            <TextField
                                id={ `device-config-${inputParameter.fieldName}` }
                                key={ inputParameter.fieldName }
                                labelKey={ `DEVICE_CONFIGS.DIALOG.PARAMETER.${inputParameter.labelKey}` }
                                name={ `properties.params.${inputParameter.fieldName}` }
                                type="number"
                                min={ inputParameter.min }
                                max={ inputParameter.max }
                                readOnly={ readOnly }
                                value={ Number(config.properties.params[inputParameter.fieldName] || Math.max(inputParameter.min!, 0)) }
                                onChange={ onChange }
                            />
                        );
                    }
                    else if (inputParameter.dataType === 'boolean') {
                        return (
                            <CheckField
                                id={ `device-config-${inputParameter.fieldName}` }
                                key={ inputParameter.fieldName }
                                labelKey={ `DEVICE_CONFIGS.DIALOG.PARAMETER.${inputParameter.labelKey}` }
                                readOnly={ readOnly }
                                input={ {
                                    name: `properties.params.${inputParameter.fieldName}`,
                                    checked: !!config.properties.params[inputParameter.fieldName] || false,
                                    onChange: onChange,
                                } }
                            />
                        );
                    }
                    else {
                        return (
                            <LegacySelectField
                                id={ `device-config-${inputParameter.fieldName}` }
                                key={ inputParameter.fieldName }
                                labelKey={ `DEVICE_CONFIGS.DIALOG.PARAMETER.${inputParameter.labelKey}` }
                                readOnly={ readOnly }
                                input={ {
                                    name: `properties.params.${inputParameter.fieldName}`,
                                    value: (config.properties.params[inputParameter.fieldName] as string) || nullValue,
                                    onChange: handleChangeSelect,
                                } }
                            >
                                { inputParameter.options!.map(option =>
                                    <option
                                        key={ option.key }
                                        value={ option.value }
                                    >
                                        { t(`DEVICE_CONFIGS.DIALOG.VALUE.${option.key}`) }
                                    </option>
                                ) }
                            </LegacySelectField>
                        );
                    }
                }) }
            </div>
        </div>
    );
};
